import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, formatDate } from '@angular/common';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { SessionStorageService } from '../../services/session-storage.service';
import { MenuItem, MessageService, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ShipmentService } from "../shipment.service";
import { GeoAddress } from 'src/app/models/common/geo-address.model';
import { FormsModule } from '@angular/forms';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { PanelModule } from 'primeng/panel';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from "primeng/datepicker";
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { Location } from '../../location/location.model';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { LogisticsProjectSummary } from '../../logistics-projects/models/logistics-project.model';
import { LocaleService } from '../../i18n/locale.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { ShipmentJobStatus } from '../models/delivery/shipment-job.status';
import { DirectoryMetrics, ShipmentDirectoryRow } from '../models/shipment-directory.model';
import { chargeCells, chargeColumns, exportPartyNames } from './directory-export-columns';
import { DirectoryFilterStateService } from './directory-filter-state.service';
import { TenantScopeService } from '../../company/tenant-scope.service';

type ShipmentOptionType = 'legacyCompanies' | 'legacyCustomers' | 'crmClients' | 'crmAccounts' | 'crmContacts' | 'drivers' | 'vehicles';


@Component({
    selector: 'app-company',
    templateUrl: './shipment-company.component.html',
    styleUrls: ['./shipment-company.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToastModule,
        ProgressBarModule,
        BreadcrumbModule,
        PanelModule,
        MultiSelectModule,
        SelectModule,
        ButtonModule,
        TableModule,
        TagModule,
        ChipModule,
        TooltipModule,
        DatePickerModule,
        InputTextModule,
        InputNumberModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        LazyLocationPickerDirective
    ],
    providers: [MessageService]
})
export class ShipmentCompanyComponent implements OnInit {
    private readonly localeService = inject(LocaleService);
    private readonly tenantScope = inject(TenantScopeService);
    private readonly destroyRef = inject(DestroyRef);
    private currentCompanyId?: number;
    protected readonly app_permissions = app_permissions;

    shipmentList: ShipmentDirectoryRow[] = [];
    isLoading = true;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;
    first: number = 0;
    pageSize = 25;
    sortField = 'shipmentId';
    sortOrder = -1;
    lastTableEvent: TableLazyLoadEvent = {
        first: 0,
        rows: this.pageSize,
        sortField: 'shipmentId',
        sortOrder: -1
    };

    searchText = '';
    shipmentNumber = '';
    waybillNumber = '';
    releaseNumber = '';
    referenceText = '';
    crmClients: string[] = [];
    crmAccounts: string[] = [];
    crmContacts: string[] = [];
    legacyCompanies: string[] = [];
    legacyCustomers: string[] = [];
    drivers: string[] = [];
    vehicles: string[] = [];
    crmClientOptions: SelectItem<string>[] = [];
    crmAccountOptions: SelectItem<string>[] = [];
    crmContactOptions: SelectItem<string>[] = [];
    legacyCompanyOptions: SelectItem<string>[] = [];
    legacyCustomerOptions: SelectItem<string>[] = [];
    driverOptions: SelectItem<string>[] = [];
    vehicleOptions: SelectItem<string>[] = [];
    assignmentState: string | null = null;
    includeClosed = false;
    logisticsProjectOptions: LogisticsProjectSummary[] = [];
    selectedSourceLocation: Location | null = null;
    selectedDestinationLocation: Location | null = null;
    selectedLogisticsProject: LogisticsProjectSummary | null = null;
    pickupCity = '';
    dropoffCity = '';
    pickupDateRange: Date[] = [];
    dropoffDateRange: Date[] = [];
    createdDateRange: Date[] = [];
    selectedStatuses: string[] = [];
    showMyShipments = false;
    private directoryMetrics: DirectoryMetrics = { total: 0, booked: 0, inTransit: 0, completed: 0, assigned: 0, unassigned: 0 };

    totalRecords: number = 0;
    bookedRecords: number = 0;
    inTransitRecords: number = 0;
    completedRecords: number = 0;
    isExporting = false;
    private readonly maxExportRows = 100;
    private directoryRequestToken = 0;
    private projectOptionsRequestToken = 0;
    private optionRequestToken: Partial<Record<ShipmentOptionType, number>> = {};
    private appliedParams: Record<string, string> = { excludeShipmentStatus: 'CLOSED' };
    private appliedFilterSnapshot: Record<string, unknown> = {};
    appliedChips: { key: string; label: string }[] = [];

    constructor(private shipmentService: ShipmentService,
        private sessionStorageService: SessionStorageService,
        public permissionService: PermissionService,
        private messageService: MessageService,
        private filterState: DirectoryFilterStateService,
        private router: Router) { }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id;
        this.restoreFilters();
        if (!Object.keys(this.appliedFilterSnapshot).length) this.appliedFilterSnapshot = this.filterSnapshot();
        this.tenantScope.activeScope$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scope => {
            if (scope?.type !== 'COMPANY' || scope.companyId === this.currentCompanyId) return;
            queueMicrotask(() => {
                if (this.sessionStorageService.getCompany()?.id !== scope.companyId) return;
                this.currentCompanyId = scope.companyId;
                this.resetFilterFields();
                for (const type of ['legacyCompanies', 'legacyCustomers', 'crmClients', 'crmAccounts', 'crmContacts', 'drivers', 'vehicles'] as ShipmentOptionType[]) {
                    this.optionRequestToken[type] = (this.optionRequestToken[type] ?? 0) + 1;
                    this.setOptionItems(type, []);
                }
                this.projectOptionsRequestToken++;
                this.logisticsProjectOptions = [];
                this.appliedParams = { excludeShipmentStatus: 'CLOSED' };
                this.appliedChips = [];
                this.appliedFilterSnapshot = {};
                this.sortField = 'shipmentId';
                this.sortOrder = -1;
                this.pageSize = 25;
                this.restoreFilters();
                if (!Object.keys(this.appliedFilterSnapshot).length) this.appliedFilterSnapshot = this.filterSnapshot();
                this.loadShipmentList({ first: 0, rows: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder });
            });
        });
        this.breadcrumbItems = [{ label: this.t('pages.companyShipments.breadcrumb.shipments') }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    }

    loadShipmentList($event: TableLazyLoadEvent) {
        const requestToken = ++this.directoryRequestToken;
        this.isLoading = true;
        this.lastTableEvent = $event;
        this.first = $event.first ?? 0;
        this.pageSize = $event.rows ?? this.pageSize;
        const page = Math.floor(($event.first ?? 0) / ($event.rows ?? this.pageSize));
        const size = $event.rows ?? this.pageSize;

        const eventSortField = Array.isArray($event.sortField) ? $event.sortField[0] : $event.sortField;
        const sortField = eventSortField || 'shipmentId';
        const sortDirection = ($event.sortOrder ?? -1) > 0 ? 'asc' : 'desc';
        this.sortField = sortField;
        this.sortOrder = sortDirection === 'asc' ? 1 : -1;
        this.saveFilters();

        this.shipmentService.searchShipmentDirectory(this.appliedSearchParams(), {
            page: page,
            size: size,
            sort: `${sortField},${sortDirection}`
        }).subscribe({
            next: shipmentPage => {
                if (requestToken !== this.directoryRequestToken) return;
                this.shipmentList = shipmentPage.content ?? [];
                this.totalRecords = shipmentPage.totalElements;
                this.directoryMetrics = shipmentPage.metrics ?? this.directoryMetrics;
                this.bookedRecords = this.directoryMetrics.booked;
                this.inTransitRecords = this.directoryMetrics.inTransit;
                this.completedRecords = this.directoryMetrics.completed;
                this.isLoading = false;
            },
            error: () => {
                if (requestToken !== this.directoryRequestToken) return;
                this.shipmentList = [];
                this.totalRecords = 0;
                this.directoryMetrics = { total: 0, booked: 0, inTransit: 0, completed: 0, assigned: 0, unassigned: 0 };
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load shipments. Please try again.' });
            }
        });
    }

    showDate(longDate: number) {
        return formatDate(new Date(longDate), 'dd-MM-yyyy HH:mm', 'en-US');
    }

    viewDetailPage(shipment: ShipmentDirectoryRow): void {
        this.router.navigate(
            ['/shipment/view', shipment.shipmentId]
        );
    }

    viewAdvancedPage(shipment: ShipmentDirectoryRow): void {
        this.router.navigate(['/shipment/view', shipment.shipmentId, 'advanced']);
    }

    refresh(): void {
        this.loadShipmentList(this.lastTableEvent);
    }

    toggleMyShipments(): void {
        this.showMyShipments = !this.showMyShipments;
        this.applyFilters();
    }

    createShipment(): void {
        this.router.navigate(['/shipment/create']);
    }

    openDeliveryDirectory(): void {
        this.router.navigate(['/shipment/deliveries']);
    }

    exportExcel(): void {
        if (!this.hasValidExportDateRange()) {
            return;
        }

        this.isExporting = true;
        this.shipmentService.searchShipmentDirectory({ ...this.appliedSearchParams(), includeExportFields: 'true' }, {
            page: 0,
            size: this.maxExportRows,
            sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`
        }).subscribe({
            next: shipmentPage => {
                try {
                    const shipments = (shipmentPage.content ?? []).slice(0, this.maxExportRows);
                    if (!shipments.length) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'No shipments to export',
                            detail: 'No shipments match the selected filters.'
                        });
                        return;
                    }

                    const exportRows = this.toExportRows(shipments);
                    const worksheet = XLSX.utils.json_to_sheet(exportRows);
                    worksheet['!cols'] = Array.from({ length: Object.keys(exportRows[0]).length }, () => ({ wch: 24 }));
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shipment Directory');
                    XLSX.writeFile(workbook, `shipment-directory-${this.exportDateStamp()}.xlsx`);

                    const limited = (shipmentPage.totalElements ?? shipments.length) > this.maxExportRows;
                    this.messageService.add({
                        severity: limited ? 'warn' : 'success',
                        summary: limited ? 'Export limited to 100 shipments' : 'Shipments exported',
                        detail: limited
                            ? 'Only the first 100 matching shipments were exported.'
                            : `${shipments.length} shipment${shipments.length === 1 ? '' : 's'} exported.`
                    });
                } catch (error) {
                    console.error('Failed to export shipment directory', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Export failed',
                        detail: 'The Shipment Directory Excel file could not be created.'
                    });
                } finally {
                    this.isExporting = false;
                }
            },
            error: () => {
                this.isExporting = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Export failed',
                    detail: 'Unable to retrieve the filtered shipments for export.'
                });
            }
        });
    }

    displayShipmentStatus(shipment: ShipmentDirectoryRow): string {
        return shipment.workflowState || shipment.shipmentStatus || 'DRAFT';
    }

    applyFilters() {
        this.appliedParams = this.buildUserSearchParams();
        this.appliedChips = this.buildFilterChips();
        this.appliedFilterSnapshot = this.filterSnapshot();
        this.first = 0;
        this.saveFilters();
        this.loadShipmentList({
            ...this.lastTableEvent,
            first: 0,
            rows: this.lastTableEvent.rows ?? this.pageSize
        });
    }

    clearFilters() {
        this.resetFilterFields();
        this.applyFilters();
        this.filterState.clear('shipments');
    }

    private resetFilterFields(): void {
        this.searchText = '';
        this.shipmentNumber = '';
        this.waybillNumber = '';
        this.releaseNumber = '';
        this.referenceText = '';
        this.crmClients = [];
        this.crmAccounts = [];
        this.crmContacts = [];
        this.legacyCompanies = [];
        this.legacyCustomers = [];
        this.drivers = [];
        this.vehicles = [];
        this.assignmentState = null;
        this.includeClosed = false;
        this.selectedSourceLocation = null;
        this.selectedDestinationLocation = null;
        this.selectedLogisticsProject = null;
        this.pickupCity = '';
        this.dropoffCity = '';
        this.pickupDateRange = [];
        this.dropoffDateRange = [];
        this.createdDateRange = [];
        this.selectedStatuses = [];
        this.showMyShipments = false;
    }

    getSeverity(status?: string): string {
        switch (status) {
            case 'DRAFT':
                return 'info';
            case 'IN_PROGRESS':
                return 'warn';
            case 'FULFILLED':
                return 'success';
            case 'COMPLETED':
            case 'POSTED_TO_ACCOUNTS':
                return 'primary';
            case 'INVOICED':
                return 'help';
            case 'CLOSED':
                return 'secondary';
            case 'CANCELLED':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    getLocationShortAddress(address?: GeoAddress | string) {
        if (!address) {
            return '';
        }
        if (typeof address === 'string') {
            return address;
        }
        return new GeoAddress().getGeoAddressFromObject(address).getShortAddress();
    }

    shipmentDeliveryCount(shipment: ShipmentDirectoryRow): number {
        return shipment.progress?.deliveryCount ?? 0;
    }

    shipmentAssignedDeliveryCount(_shipment: ShipmentDirectoryRow): number {
        return 0;
    }

    shipmentCompletedDeliveryCount(shipment: ShipmentDirectoryRow): number {
        return shipment.progress?.completedDeliveryCount ?? 0;
    }

    shipmentProgress(shipment: ShipmentDirectoryRow): number {
        return shipment.progress?.percent ?? 0;
    }

    shipmentProgressCaption(shipment: ShipmentDirectoryRow): string {
        const deliveryCount = this.shipmentDeliveryCount(shipment);
        if (!deliveryCount) {
            return 'Delivery assignment pending';
        }
        const completed = this.shipmentCompletedDeliveryCount(shipment);
        const progress = shipment.progress;
        return progress?.totalSites
            ? `${completed}/${deliveryCount} deliveries completed · ${progress.completedSites}/${progress.totalSites} sites complete`
            : `${completed}/${deliveryCount} deliveries completed`;
    }

    shipmentCargoSummary(shipment: ShipmentDirectoryRow): string {
        return shipment.cargoSummary || 'Cargo details pending';
    }

    shipmentContextReference(shipment: ShipmentDirectoryRow): string {
        return shipment.externalReference
            || shipment.customerOrderNumber
            || shipment.primaryTransportReleaseNumber
            || 'No customer reference';
    }

    shipmentProjectLabel(shipment: ShipmentDirectoryRow): string {
        if (shipment.primaryTransportReleaseNumber) {
            return `Release ${shipment.primaryTransportReleaseNumber}`;
        }
        if (shipment.logisticsProjectId) {
            return `Logistics project #${shipment.logisticsProjectId}`;
        }
        return 'Standalone shipment';
    }

    latestDeliveryStatus(shipment: ShipmentDirectoryRow): string {
        return shipment.progress?.latestStatus || 'Assignment pending';
    }

    latestOperationalUpdate(shipment: ShipmentDirectoryRow): number | undefined {
        return shipment.progress?.latestUpdatedAt || shipment.workflowUpdatedAt || shipment.createdAt;
    }

    formatOperationalStatus(status?: string): string {
        return (status || 'Pending')
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, character => character.toUpperCase());
    }

    deliveryStatusSeverity(status?: string): string {
        const normalized = (status || '').toUpperCase();
        if (this.isCompletedDeliveryStatus(normalized)) return 'success';
        if (['JOB_DRIVER_REJECTED', 'JOB_DRIVER_CANCELLED', 'CANCELLED', 'EXCEPTION'].includes(normalized)) return 'danger';
        if (['DRIVER_DELAY_ON_TRIP', 'SHORT', 'DAMAGED'].includes(normalized)) return 'warn';
        if (normalized === 'ASSIGNMENT PENDING' || !normalized) return 'secondary';
        return 'info';
    }

    shipmentMetrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.companyShipments.metrics.totalShipments'), value: this.totalRecords, hint: this.t('pages.companyShipments.metrics.totalShipmentsHint'), tone: 'info' },
            { label: this.t('pages.companyShipments.metrics.booked'), value: this.bookedRecords, hint: this.t('pages.companyShipments.metrics.bookedHint'), tone: 'warn' },
            { label: this.t('pages.companyShipments.metrics.inTransit'), value: this.inTransitRecords, hint: this.t('pages.companyShipments.metrics.inTransitHint'), tone: 'info' },
            { label: this.t('pages.companyShipments.metrics.completed'), value: this.completedRecords, hint: this.t('pages.companyShipments.metrics.completedHint'), tone: 'success' }
        ];
    }

    activeFilterCount(): number {
        return this.appliedChips.length;
    }

    closedShipmentsExcluded(): boolean {
        return this.appliedParams['excludeShipmentStatus'] === 'CLOSED';
    }

    resultText(): string {
        this.localeService.locale();
        return this.t('pages.companyShipments.results.shipments', { count: this.totalRecords });
    }

    currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.companyShipments.table.pageReport');
    }

    statusLabel(status?: string): string {
        this.localeService.locale();
        const normalizedStatus = (status ?? '').toUpperCase();
        if (!normalizedStatus) return this.t('pages.companyShipments.status.draft');
        return this.localeService.translateIfPresent(`pages.companyShipments.statusMap.${normalizedStatus.toLowerCase()}`, status ?? normalizedStatus);
    }

    statusOptions(): { label: string; value: string; icon: string }[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.companyShipments.status.draft'), value: 'DRAFT', icon: 'pi pi-pencil' },
            { label: this.t('pages.companyShipments.status.inProgress'), value: 'IN_PROGRESS', icon: 'pi pi-spin pi-spinner' },
            { label: this.t('pages.companyShipments.status.fulfilled'), value: 'FULFILLED', icon: 'pi pi-check-square' },
            { label: this.t('pages.companyShipments.status.completed'), value: 'COMPLETED', icon: 'pi pi-check-circle' },
            { label: this.t('pages.companyShipments.status.postedToAccounts'), value: 'POSTED_TO_ACCOUNTS', icon: 'pi pi-file-export' },
            { label: this.t('pages.companyShipments.status.invoiced'), value: 'INVOICED', icon: 'pi pi-file' },
            { label: this.t('pages.companyShipments.status.closed'), value: 'CLOSED', icon: 'pi pi-lock' },
            { label: this.t('pages.companyShipments.status.cancelled'), value: 'CANCELLED', icon: 'pi pi-times-circle' }
        ];
    }

    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        this.localeService.locale();
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }

    private appliedSearchParams(): { [param: string]: string } {
        const params = { ...this.appliedParams };
        params['owningCompanyId'] = this.sessionStorageService.getCompany()?.id?.toString() ?? '';
        return params;
    }

    private buildUserSearchParams(): { [param: string]: string } {
        const params: { [param: string]: string } = {};

        this.setParam(params, 'searchText', this.searchText);
        this.setParam(params, 'shipmentNumber', this.shipmentNumber);
        this.setParam(params, 'waybillNumber', this.waybillNumber);
        this.setParam(params, 'releaseNumber', this.releaseNumber);
        this.setParam(params, 'referenceText', this.referenceText);
        this.setParam(params, 'crmClientList', this.crmClients.join(','));
        this.setParam(params, 'crmAccountList', this.crmAccounts.join(','));
        this.setParam(params, 'crmContactList', this.crmContacts.join(','));
        this.setParam(params, 'driverList', this.drivers.join(','));
        this.setParam(params, 'vehicleList', this.vehicles.join(','));
        this.setParam(params, 'assignmentState', this.assignmentState);
        if (this.showMyShipments) {
            params['createdByCurrentUser'] = 'true';
        }
        if (this.selectedStatuses.length) {
            this.setParam(params, 'shipmentStatus', this.selectedStatuses.join(','));
        } else if (!this.includeClosed) {
            this.setParam(params, 'excludeShipmentStatus', 'CLOSED');
        }
        this.setParam(params, 'legacyCustomerList', this.legacyCustomers.join(','));
        this.setParam(params, 'legacyCompanyList', this.legacyCompanies.join(','));
        this.setParam(params, 'sourceLocationId', this.selectedSourceLocation?.id);
        this.setParam(params, 'destinationLocationId', this.selectedDestinationLocation?.id);
        this.setParam(params, 'logisticsProjectId', this.selectedLogisticsProject?.projectId);
        this.setParam(params, 'pickupCity', this.pickupCity);
        this.setParam(params, 'dropoffCity', this.dropoffCity);
        this.setDateRangeParams(params, 'pickupDate', this.pickupDateRange);
        this.setDateRangeParams(params, 'dropoffDate', this.dropoffDateRange);
        this.setDateRangeParams(params, 'createdDate', this.createdDateRange);

        return params;
    }

    private hasValidExportDateRange(): boolean {
        const applied = this.appliedParams;
        const asDate = (value?: string): Date | undefined => value ? new Date(`${value}T00:00:00`) : undefined;
        const ranges = [
            { label: 'pickup', range: [asDate(applied['pickupDateFrom']), asDate(applied['pickupDateTo'])] },
            { label: 'delivery', range: [asDate(applied['dropoffDateFrom']), asDate(applied['dropoffDateTo'])] }
        ].filter(({ range }) => range.some(Boolean));

        if (!ranges.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Date range required',
                detail: 'Select a complete pickup or delivery date range before exporting.'
            });
            return false;
        }

        for (const { label, range } of ranges) {
            const [start, end] = range;
            if (!this.isValidExportDate(start) || !this.isValidExportDate(end)) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Complete date range required',
                    detail: `Select both dates for the ${label} range before exporting.`
                });
                return false;
            }
            if (end!.getTime() < start!.getTime()) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Invalid date range',
                    detail: `The ${label} end date must be on or after the start date.`
                });
                return false;
            }
            if (end!.getTime() - start!.getTime() > 31 * 24 * 60 * 60 * 1000) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Date range too large',
                    detail: 'Exports are limited to a date range of one month or less.'
                });
                return false;
            }
        }

        return true;
    }

    private toExportRows(shipments: ShipmentDirectoryRow[]): Record<string, string | number>[] {
        const columns = chargeColumns(shipments);
        return shipments.map(shipment => ({
            'Reference Number': shipment.shipmentNumber || '',
            'External Reference Number': shipment.externalReference || '',
            'Customer Reference': shipment.customerOrderNumber || '',
            ...exportPartyNames(shipment),
            'Pickup Location': shipment.sourceLocation?.name || '',
            'Pickup Address': this.getLocationShortAddress(shipment.sourceLocation?.address),
            'Drop-off Location': shipment.destinationLocation?.name || '',
            'Drop-off Address': this.getLocationShortAddress(shipment.destinationLocation?.address),
            'Pickup Time': this.formatExportDate(shipment.requestedBookingTime),
            'Delivery Time': '',
            Status: this.statusLabel(this.displayShipmentStatus(shipment)),
            Priority: 'NORMAL',
            'Cargo Description': this.shipmentCargoSummary(shipment),
            'Vehicle Type': shipment.equipmentLabel || '',
            'Vehicle Configuration': '',
            'Vehicle Capacity': '',
            'Service Category': this.formatOperationalStatus(shipment.serviceCategory),
            'Route Type': this.formatOperationalStatus(shipment.routeType),
            'Created On': this.formatExportDate(shipment.createdAt),
            'Selling Unit Price': shipment.sellingUnitPrice ?? '',
            'Selling Base Total': shipment.sellingBaseTotal ?? '',
            'Selling Currency': shipment.currencyCode || '',
            ...chargeCells(shipment, columns)
        }));
    }

    private formatExportDate(value: unknown): string {
        if (value == null) {
            return '';
        }
        const date = value instanceof Date ? value : new Date(value as string | number);
        return Number.isNaN(date.getTime()) ? '' : formatDate(date, 'dd-MM-yyyy HH:mm', 'en-US');
    }

    private exportDateStamp(): string {
        return formatDate(new Date(), 'yyyyMMdd-HHmm', 'en-US');
    }

    private isValidExportDate(value: unknown): value is Date {
        return value instanceof Date && !Number.isNaN(value.getTime());
    }

    private setParam(params: { [param: string]: string }, key: string, value: string | number | null | undefined): void {
        if (value == null) {
            return;
        }
        const normalized = value.toString().trim();
        if (normalized) {
            params[key] = normalized;
        }
    }

    private setDateRangeParams(params: { [param: string]: string }, key: 'pickupDate' | 'dropoffDate' | 'createdDate', range: Date[] = []): void {
        if (range[0]) {
            params[`${key}From`] = this.formatDateParam(range[0]);
        }
        if (range[1]) {
            params[`${key}To`] = this.formatDateParam(range[1]);
        }
    }

    private formatDateParam(date: Date): string {
        return formatDate(date, 'yyyy-MM-dd', 'en-US');
    }

    private isCompletedDeliveryStatus(status?: string): boolean {
        return [
            ShipmentJobStatus.DRIVER_JOB_COMPLETED,
            ShipmentJobStatus.JOB_COMPLETION_ACKNOWLEDGE,
            'COMPLETED',
            'DELIVERED'
        ].includes((status || '').toUpperCase() as ShipmentJobStatus);
    }

    loadLogisticsProjectOptions(search = ''): void {
        const owningCompanyId = this.sessionStorageService.getCompany()?.id;
        if (!owningCompanyId) {
            this.logisticsProjectOptions = [];
            return;
        }
        const requestToken = ++this.projectOptionsRequestToken;
        this.shipmentService.getDirectoryOptions('projects', owningCompanyId, search, 0, 25)
            .subscribe(response => {
                if (requestToken !== this.projectOptionsRequestToken) return;
                const projects = (response.items ?? []).map(option => ({
                    projectId: option.id,
                    projectName: option.label,
                    projectNumber: option.detail
                } as LogisticsProjectSummary));
                this.logisticsProjectOptions = this.withSelectedProject(projects);
            });
    }

    private withSelectedProject(options: LogisticsProjectSummary[]): LogisticsProjectSummary[] {
        if (!this.selectedLogisticsProject || options.some(option => option.projectId === this.selectedLogisticsProject?.projectId)) {
            return options;
        }
        return [this.selectedLogisticsProject, ...options];
    }

    loadFilterOptions(type: ShipmentOptionType, search = ''): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        const token = (this.optionRequestToken[type] ?? 0) + 1;
        this.optionRequestToken[type] = token;
        this.shipmentService.getDirectoryOptions(type, companyId, search, 0, 25).subscribe(response => {
            if (this.optionRequestToken[type] !== token) return;
            const previous = this.optionItems(type);
            const selected = new Set(this.selectedIds(type));
            const items = [...previous.filter(item => selected.has(item.value)),
                ...(response.items ?? []).map(item => ({ label: item.label, value: String(item.id) }))];
            this.setOptionItems(type, [...new Map(items.map(item => [item.value, item])).values()]);
        });
    }

    removeFilter(key: string): void {
        this.restoreFilters();
        switch (key) {
            case 'searchText': this.searchText = ''; break;
            case 'shipmentNumber': this.shipmentNumber = ''; break;
            case 'waybillNumber': this.waybillNumber = ''; break;
            case 'releaseNumber': this.releaseNumber = ''; break;
            case 'referenceText': this.referenceText = ''; break;
            case 'crmClients': this.crmClients = []; break;
            case 'crmAccounts': this.crmAccounts = []; break;
            case 'crmContacts': this.crmContacts = []; break;
            case 'drivers': this.drivers = []; break;
            case 'vehicles': this.vehicles = []; break;
            case 'assignmentState': this.assignmentState = null; break;
            case 'selectedStatuses': this.selectedStatuses = []; break;
            case 'legacyCompanies': this.legacyCompanies = []; break;
            case 'legacyCustomers': this.legacyCustomers = []; break;
            case 'selectedSourceLocation': this.selectedSourceLocation = null; break;
            case 'selectedDestinationLocation': this.selectedDestinationLocation = null; break;
            case 'selectedLogisticsProject': this.selectedLogisticsProject = null; break;
            case 'pickupCity': this.pickupCity = ''; break;
            case 'dropoffCity': this.dropoffCity = ''; break;
            case 'pickupDateRange': this.pickupDateRange = []; break;
            case 'dropoffDateRange': this.dropoffDateRange = []; break;
            case 'createdDateRange': this.createdDateRange = []; break;
            case 'showMyShipments': this.showMyShipments = false; break;
            case 'includeClosed': this.includeClosed = false; break;
        }
        this.applyFilters();
    }

    private buildFilterChips(): { key: string; label: string }[] {
        const chips: { key: string; label: string }[] = [];
        const add = (key: string, title: string, value: unknown) => {
            if (value) chips.push({ key, label: `${title}: ${value}` });
        };
        add('searchText', this.t('pages.companyShipments.search.keywordSearch'), this.searchText.trim());
        add('shipmentNumber', this.t('pages.companyShipments.search.shipmentNumber'), this.shipmentNumber.trim());
        add('waybillNumber', this.t('pages.companyShipments.search.waybillNumber'), this.waybillNumber.trim());
        add('releaseNumber', this.t('pages.companyShipments.search.releaseNumber'), this.releaseNumber.trim());
        add('referenceText', this.t('pages.companyShipments.search.referenceText'), this.referenceText.trim());
        for (const type of ['legacyCompanies', 'legacyCustomers', 'crmClients', 'crmAccounts', 'crmContacts', 'drivers', 'vehicles'] as ShipmentOptionType[]) {
            const ids = this.selectedIds(type);
            const labelKey = { legacyCompanies: 'legacyCompany', legacyCustomers: 'legacyCustomer', crmClients: 'crmClients',
                crmAccounts: 'crmAccounts', crmContacts: 'crmContacts', drivers: 'drivers', vehicles: 'vehicles' }[type];
            if (ids.length) add(type, this.t(`pages.companyShipments.search.${labelKey}`),
                ids.map(id => this.optionItems(type).find(item => item.value === id)?.label ?? id).join(', '));
        }
        add('assignmentState', this.t('pages.companyShipments.search.assignment'), this.assignmentState);
        add('selectedStatuses', this.t('pages.companyShipments.search.status'), this.selectedStatuses.join(', '));
        add('selectedSourceLocation', this.t('pages.companyShipments.search.sourceLocation'), this.selectedSourceLocation?.name);
        add('selectedDestinationLocation', this.t('pages.companyShipments.search.destinationLocation'), this.selectedDestinationLocation?.name);
        add('selectedLogisticsProject', this.t('pages.companyShipments.search.logisticsProject'), this.selectedLogisticsProject?.projectName);
        add('pickupCity', this.t('pages.companyShipments.search.pickupCity'), this.pickupCity.trim());
        add('dropoffCity', this.t('pages.companyShipments.search.dropoffCity'), this.dropoffCity.trim());
        for (const [key, label, range] of [['pickupDateRange', this.t('pages.companyShipments.search.pickupDateRange'), this.pickupDateRange],
            ['dropoffDateRange', this.t('pages.companyShipments.search.dropoffDateRange'), this.dropoffDateRange],
            ['createdDateRange', this.t('pages.companyShipments.search.createdDateRange'), this.createdDateRange]] as [string, string, Date[]][]) {
            if (range.length) add(key, label, range.map(date => date && formatDate(date, 'yyyy-MM-dd', 'en-US')).filter(Boolean).join(' – '));
        }
        if (this.showMyShipments) chips.push({ key: 'showMyShipments', label: this.t('pages.companyShipments.hero.myShipments') });
        if (this.includeClosed) chips.push({ key: 'includeClosed', label: this.t('pages.companyShipments.search.includeClosed') });
        return chips;
    }

    private selectedIds(type: ShipmentOptionType): string[] { return this[type]; }
    private optionItems(type: ShipmentOptionType): SelectItem<string>[] {
        switch (type) {
            case 'legacyCompanies': return this.legacyCompanyOptions;
            case 'legacyCustomers': return this.legacyCustomerOptions;
            case 'crmClients': return this.crmClientOptions;
            case 'crmAccounts': return this.crmAccountOptions;
            case 'crmContacts': return this.crmContactOptions;
            case 'drivers': return this.driverOptions;
            case 'vehicles': return this.vehicleOptions;
        }
    }
    private setOptionItems(type: ShipmentOptionType, items: SelectItem<string>[]): void {
        switch (type) {
            case 'legacyCompanies': this.legacyCompanyOptions = items; break;
            case 'legacyCustomers': this.legacyCustomerOptions = items; break;
            case 'crmClients': this.crmClientOptions = items; break;
            case 'crmAccounts': this.crmAccountOptions = items; break;
            case 'crmContacts': this.crmContactOptions = items; break;
            case 'drivers': this.driverOptions = items; break;
            case 'vehicles': this.vehicleOptions = items; break;
        }
    }

    private saveFilters(): void {
        this.filterState.save('shipments', {
            filters: this.appliedFilterSnapshot,
            options: Object.fromEntries((['legacyCompanies', 'legacyCustomers', 'crmClients', 'crmAccounts', 'crmContacts', 'drivers', 'vehicles'] as ShipmentOptionType[])
                .map(type => [type, this.optionItems(type).filter(item => (this.appliedFilterSnapshot[type] as string[] || []).includes(item.value))])),
            sortField: this.sortField, sortOrder: this.sortOrder, rows: this.pageSize
        });
    }

    private filterSnapshot(): Record<string, unknown> {
        const dateRange = (range: Date[]) => range.map(date => date ? formatDate(date, 'yyyy-MM-dd', 'en-US') : null);
        return {
                searchText: this.searchText, shipmentNumber: this.shipmentNumber, waybillNumber: this.waybillNumber,
                releaseNumber: this.releaseNumber, referenceText: this.referenceText,
                legacyCompanies: [...this.legacyCompanies], legacyCustomers: [...this.legacyCustomers],
                crmClients: [...this.crmClients], crmAccounts: [...this.crmAccounts], crmContacts: [...this.crmContacts],
                drivers: [...this.drivers], vehicles: [...this.vehicles], assignmentState: this.assignmentState,
                selectedStatuses: [...this.selectedStatuses], selectedSourceLocation: this.selectedSourceLocation,
                selectedDestinationLocation: this.selectedDestinationLocation, selectedLogisticsProject: this.selectedLogisticsProject,
                pickupCity: this.pickupCity, dropoffCity: this.dropoffCity,
                pickupDateRange: dateRange(this.pickupDateRange), dropoffDateRange: dateRange(this.dropoffDateRange),
                createdDateRange: dateRange(this.createdDateRange), showMyShipments: this.showMyShipments,
                includeClosed: this.includeClosed
        };
    }

    private restoreFilters(): void {
        const saved = this.filterState.load('shipments') as {
            filters?: any; options?: Record<string, SelectItem<string>[]>;
            sortField?: string; sortOrder?: number; rows?: number;
        } | null;
        const f = saved?.filters;
        if (!f || typeof f !== 'object') return;
        const string = (value: unknown) => typeof value === 'string' ? value.slice(0, 250) : '';
        const ids = (value: unknown): string[] => Array.isArray(value)
            ? value.filter(item => typeof item === 'string' && /^\d+$/.test(item)).slice(0, 50) : [];
        const dates = (value: unknown): Date[] => Array.isArray(value) ? value.slice(0, 2).map(item => {
            if (typeof item !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(item)) return null;
            const [year, month, day] = item.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
        }) as Date[] : [];
        for (const key of ['searchText', 'shipmentNumber', 'waybillNumber', 'releaseNumber', 'referenceText', 'pickupCity', 'dropoffCity'] as const) this[key] = string(f[key]);
        for (const key of ['legacyCompanies', 'legacyCustomers', 'crmClients', 'crmAccounts', 'crmContacts', 'drivers', 'vehicles'] as const) this[key] = ids(f[key]);
        this.selectedStatuses = Array.isArray(f.selectedStatuses) ? f.selectedStatuses.filter((item: unknown) => typeof item === 'string' && this.statusOptions().some(option => option.value === item)) : [];
        this.assignmentState = ['ASSIGNED', 'UNASSIGNED'].includes(f.assignmentState) ? f.assignmentState : null;
        this.selectedSourceLocation = Number.isInteger(f.selectedSourceLocation?.id) ? f.selectedSourceLocation : null;
        this.selectedDestinationLocation = Number.isInteger(f.selectedDestinationLocation?.id) ? f.selectedDestinationLocation : null;
        this.selectedLogisticsProject = Number.isInteger(f.selectedLogisticsProject?.projectId) ? f.selectedLogisticsProject : null;
        this.pickupDateRange = dates(f.pickupDateRange);
        this.dropoffDateRange = dates(f.dropoffDateRange);
        this.createdDateRange = dates(f.createdDateRange);
        this.showMyShipments = f.showMyShipments === true;
        this.includeClosed = f.includeClosed === true;
        for (const type of ['legacyCompanies', 'legacyCustomers', 'crmClients', 'crmAccounts', 'crmContacts', 'drivers', 'vehicles'] as ShipmentOptionType[]) {
            const options = saved?.options?.[type];
            this.setOptionItems(type, Array.isArray(options) ? options.filter((item: SelectItem<string>) =>
                this.selectedIds(type).includes(item.value) && typeof item.label === 'string').slice(0, 50) : []);
        }
        const savedSortField = saved?.sortField ?? '';
        this.sortField = ['shipmentId', 'shipmentNumber', 'requestedBookingTime', 'workflowUpdatedAt'].includes(savedSortField) ? savedSortField : 'shipmentId';
        this.sortOrder = saved?.sortOrder === 1 ? 1 : -1;
        const savedRows = saved?.rows ?? 25;
        this.pageSize = [10, 25, 50].includes(savedRows) ? savedRows : 25;
        this.lastTableEvent = { first: 0, rows: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder };
        this.appliedParams = this.buildUserSearchParams();
        this.appliedChips = this.buildFilterChips();
        this.appliedFilterSnapshot = this.filterSnapshot();
    }

}
